import { useCountUp } from "../../hooks/useCountUp";

/**
 * The signature calorie-bank dial — an animated circular progress readout in
 * the brand's fintech style. The arc sweeps to `value/max` and the number
 * counts up beneath it.
 */
export function CalorieDial({
  value,
  max,
  size = 220,
  label = "CAL BANKED",
  active = true,
  theme = "dark",
}: {
  value: number;
  max: number;
  size?: number;
  label?: string;
  active?: boolean;
  /** "dark" for the in-phone screen; "light" when the dial sits on the page. */
  theme?: "dark" | "light";
}) {
  const shown = useCountUp(value, active, 1000);
  const pct = Math.max(0, Math.min(1, value / max));
  const stroke = size * 0.075;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * pct;
  const trackColor = theme === "light" ? "#E2E8F0" : "#16305a";
  const numberCls = theme === "light" ? "text-navy" : "text-cloud";
  const labelCls =
    theme === "light" ? "text-charcoal/55" : "text-teal-mist/70";

  return (
    <div
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="dialGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#14B8A6" />
            <stop offset="60%" stopColor="#0D9488" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="url(#dialGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: "stroke-dasharray 1s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div
            className={`num font-bold ${numberCls}`}
            style={{ fontSize: size * 0.17 }}
          >
            {Math.round(shown).toLocaleString()}
          </div>
          <div
            className={`mt-1 font-mono uppercase tracking-[0.2em] ${labelCls}`}
            style={{ fontSize: size * 0.05 }}
          >
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}
